// @flow

import * as React from 'react';

type Props<T> = {|
  from: T,
  tension?: number,
  mass?: number,
  friction?: number,
  onUpdate: T => void,
|};

type Hook<T> = {|
  set: (values: T, onEnd?: () => void) => void,
  stop: () => void,
|};

type Value = {|
  to: number,
  position: number,
  velocity: number,
|};

type Values = {|
  [string]: number,
|};

type Animation = {|
  tension: number,
  friction: number,
  mass: number,
  values: Map<string, Value>,
  update: null | ((data: any, finished: boolean) => void),
|};

const useLayoutEffect =
  typeof window === 'undefined' ? React.useEffect : React.useLayoutEffect;

let id = -1;
let animationFrame = null;
const animations = new Map<number, Animation>();
const activeIds = new Set<number>();
let lastTime = 0;

const loop = () => {
  if (activeIds.size !== 0 && animationFrame == null) {
    animationFrame = requestAnimationFrame(newTime => {
      // skip frame if loop was interrupted to get proper time delta
      if (newTime > lastTime + 64) {
        lastTime = newTime;
        animationFrame = null;
        loop();
        return;
      }
      // http://gafferongames.com/game-physics/fix-your-timestep/
      const numSteps = Math.floor(newTime - lastTime);
      for (const [id, animation] of animations) {
        const { tension, friction, mass, values, update } = animation;
        const data = {};
        let finished = true;
        for (const [key, value] of values) {
          const { to } = value;
          let { position, velocity } = value;
          for (let i = 0; i < numSteps; i += 1) {
            const force = -tension * (position - to);
            const damping = -friction * velocity;
            const acceleration = (force + damping) / mass;
            const timedelta = 1;
            velocity = velocity + (acceleration * timedelta) / 1000;
            position = position + (velocity * timedelta) / 1000;
          }
          const precision = 0.00001;
          if (
            Math.abs(value.position - position) > precision ||
            Math.abs(velocity) > precision
          ) {
            finished = false;
          }
          data[key] = position;
          value.position = position;
          value.velocity = velocity;
        }
        if (finished) {
          activeIds.delete(id);
          animation.update = null;
        }
        if (update) {
          update(data, finished);
        }
      }
      animationFrame = null;
      // loop is interrupted when activeIds is empty
      loop();
    });
  }
};

const createAnimation = (id, from: Values, tension, friction, mass) => {
  if (animations.has(id) === false) {
    const values = new Map();
    for (const key of Object.keys(from)) {
      const position = from[key];
      values.set(key, {
        to: position,
        position,
        velocity: 0,
      });
    }
    animations.set(id, {
      tension,
      friction,
      mass,
      values,
      update: null,
    });
  }
};

const setAnimation = (id, to: Values, update) => {
  const currentAnimation = animations.get(id);
  if (currentAnimation != null) {
    for (const [key, currentValue] of currentAnimation.values) {
      currentValue.to = to[key];
    }
    currentAnimation.update = update;
  }
  activeIds.add(id);
  loop();
};

const stopAnimation = id => {
  activeIds.delete(id);
  if (activeIds.size !== 0 && animationFrame != null) {
    cancelAnimationFrame(animationFrame);
    animationFrame = null;
  }
};

export const useSpring = <T: Values>({
  from,
  tension = 170, // or tension - spring energetic load
  friction = 26, // or friction - spring resistence
  mass = 1, // spring mass
  onUpdate,
}: Props<T>): Hook<T> => {
  // compute unique id
  const idRef = React.useRef(-1);
  if (idRef.current === -1) {
    id += 1;
    idRef.current = id;
  }
  // stabilize update callback
  const onUpdateRef = React.useRef(onUpdate);
  useLayoutEffect(() => {
    onUpdateRef.current = onUpdate;
  });
  // stabilize from value
  const fromRef = React.useRef<T>(from);
  useLayoutEffect(() => {
    // apply initial styles
    onUpdateRef.current(fromRef.current);
    createAnimation(idRef.current, fromRef.current, tension, friction, mass);
    return () => {
      animations.delete(id);
    };
  }, [tension, friction, mass]);
  // start new animation with values or continue existing with new target
  const set = React.useCallback((to, onEnd) => {
    setAnimation(idRef.current, to, (data, finished) => {
      if (finished) {
        onUpdateRef.current(data);
        onEnd?.();
      } else {
        onUpdateRef.current(data);
      }
    });
  }, []);
  // interrupt current animation
  const stop = React.useCallback(() => {
    stopAnimation(idRef.current);
  }, []);
  // clean resources on unmount
  React.useEffect(() => {
    return stop;
  }, [stop]);
  return { set, stop };
};
